import { useState } from 'react';

import { isAxiosError } from 'axios';

import { type AdminMember } from '@/api/queries/admin';
import { teamApi } from '@/api/queries/team';
import { currentTenantName, useTenantStore } from '@/auth/tenantStore';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { useModal } from '@/components/modal/useModal';
import { INVITE_ROLE_HINT } from '@/components/team/inviteRoleHint';
import { MembersTable } from '@/components/team/MembersTable';
import { Button, Field, Icon, Input, Modal, useToast, type IconName } from '@/components/ui';

function inviteErrorTitle(err: unknown): string {
  if (isAxiosError(err) && err.response?.status === 409) {
    return 'This email is already an active member of this tenant.';
  }
  return 'Failed to invite';
}

export function TeamPage() {
  const tenantName = useTenantStore(currentTenantName);
  const toast = useToast();
  const { openModal } = useModal();
  const { data: members, isPending, isError, error } = teamApi.useTeamMembers();
  const deactivate = teamApi.useDeactivateTeamMember();
  const remove = teamApi.useRemoveTeamMember();
  const [inviteOpen, setInviteOpen] = useState(false);

  const onDeactivate = (m: AdminMember) => {
    openModal(
      <ConfirmDialog
        title="Deactivate member"
        message={`Deactivate ${m.invitedEmail ?? 'this member'}? They will lose access to this tenant.`}
        confirmLabel="Deactivate"
        onConfirm={async () => {
          try {
            await deactivate.mutateAsync({ membershipId: m.id });
            toast.show({ title: 'Member deactivated', severity: 'success' });
          } catch (err) {
            toast.show({
              title: 'Failed to deactivate',
              description: err instanceof Error ? err.message : undefined,
              severity: 'error',
            });
          }
        }}
      />,
    );
  };

  const onRemove = (m: AdminMember) => {
    openModal(
      <ConfirmDialog
        title="Remove member"
        message={`Remove ${m.invitedEmail ?? 'this member'}? Their membership row will be deleted.`}
        confirmLabel="Remove"
        onConfirm={async () => {
          try {
            await remove.mutateAsync({ membershipId: m.id });
            toast.show({ title: 'Member removed', severity: 'success' });
          } catch (err) {
            toast.show({
              title: 'Failed to remove',
              description: err instanceof Error ? err.message : undefined,
              severity: 'error',
            });
          }
        }}
      />,
    );
  };

  if (isPending) return <LoadingSpinner message="Loading team..." />;

  const forbidden = isAxiosError(error) && error.response?.status === 403;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 16,
        }}
      >
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>Team</h1>
          <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 4 }}>
            {tenantName ? `Members of ${tenantName}` : 'Members of this tenant'}
          </div>
        </div>
        <Button
          variant="primary"
          leftIcon={<Icon name="plus" size={14} />}
          onClick={() => setInviteOpen(true)}
        >
          Invite member
        </Button>
      </div>

      {forbidden ? (
        <TeamNotice
          icon="shield"
          title="No access to this tenant's team"
          body="Managing this tenant's team needs the Datahub admin role and an active membership in this tenant. Switch to a tenant where you're a member, or ask for the role in UDB."
        />
      ) : isError ? (
        <TeamNotice
          icon="alert"
          title="Couldn't load the team"
          body={error instanceof Error ? error.message : 'Please try again in a moment.'}
        />
      ) : (
        <MembersTable
          members={members ?? []}
          onDeactivate={onDeactivate}
          onRemove={onRemove}
        />
      )}

      {inviteOpen && <InviteTeamMemberModal onClose={() => setInviteOpen(false)} />}
    </div>
  );
}

function TeamNotice({ icon, title, body }: { icon: IconName; title: string; body: string }) {
  return (
    <div
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        padding: '32px 24px',
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
      }}
    >
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: 'var(--radius-sm)',
          background: 'var(--brand-soft)',
          color: 'var(--brand)',
          display: 'grid',
          placeItems: 'center',
          marginBottom: 4,
        }}
      >
        <Icon name={icon} size={20} />
      </div>
      <div style={{ fontSize: 14, fontWeight: 600 }}>{title}</div>
      <div style={{ fontSize: 12.5, color: 'var(--text-3)', maxWidth: 420, lineHeight: 1.5 }}>
        {body}
      </div>
    </div>
  );
}

function InviteTeamMemberModal({ onClose }: { onClose: () => void }) {
  const invite = teamApi.useInviteTeamMember();
  const toast = useToast();
  const [email, setEmail] = useState('');

  const submit = async () => {
    const trimmed = email.trim();
    if (!trimmed) return;
    try {
      await invite.mutateAsync({ email: trimmed });
      toast.show({ title: `Invited ${trimmed}`, severity: 'success' });
      onClose();
    } catch (err) {
      toast.show({
        title: inviteErrorTitle(err),
        description: err instanceof Error ? err.message : undefined,
        severity: 'error',
      });
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Invite member"
      size="sm"
      footer={
        <>
          <span style={{ flex: 1 }} />
          <Button variant="ghost" onClick={onClose} disabled={invite.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            loading={invite.isPending}
            disabled={!email.trim()}
          >
            Invite
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Field label="Email" required hint={INVITE_ROLE_HINT}>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="someone@example.com"
            autoFocus
          />
        </Field>
      </div>
    </Modal>
  );
}
